/** Shared image upload limits and crop presets for profile, circle, and course branding. */

export const IMAGE_UPLOAD_ACCEPT = 'image/jpeg,image/png,image/webp';

export const IMAGE_UPLOAD_ACCEPT_LABEL = 'JPEG, PNG, or WebP';

/** Matches social post / profile media API limit. */
export const IMAGE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

export const IMAGE_UPLOAD_MAX_BYTES_LABEL = '10 MB';

export type ImageUploadPresetId =
  | 'profile-avatar'
  | 'profile-banner'
  | 'circle-cover'
  | 'circle-logo'
  | 'course-cover';

export type ImageUploadPreviewVariant = 'banner' | 'avatar' | 'logo';

export type ImageUploadPreset = {
  id: ImageUploadPresetId;
  label: string;
  cropTitle: string;
  cropDescription: string;
  aspect: number;
  outputWidth: number;
  outputHeight: number;
  minWidth: number;
  minHeight: number;
  maxBytes: number;
  hint: string;
  previewVariant: ImageUploadPreviewVariant;
};

const BANNER_PRESET_BASE = {
  aspect: 3,
  outputWidth: 1500,
  outputHeight: 500,
  minWidth: 900,
  minHeight: 300,
  maxBytes: IMAGE_UPLOAD_MAX_BYTES,
  hint: `Wide banner · ${1500}×${500} recommended · Max ${IMAGE_UPLOAD_MAX_BYTES_LABEL}`,
  previewVariant: 'banner' as const,
};

const SQUARE_LOGO_PRESET_BASE = {
  aspect: 1,
  outputWidth: 512,
  outputHeight: 512,
  minWidth: 256,
  minHeight: 256,
  maxBytes: IMAGE_UPLOAD_MAX_BYTES,
  hint: `Square · ${512}×${512} recommended · Max ${IMAGE_UPLOAD_MAX_BYTES_LABEL}`,
  previewVariant: 'logo' as const,
};

export const IMAGE_UPLOAD_PRESETS: Record<ImageUploadPresetId, ImageUploadPreset> = {
  'profile-avatar': {
    id: 'profile-avatar',
    label: 'Profile picture',
    cropTitle: 'Adjust profile picture',
    cropDescription: 'Drag to reposition and use the slider to zoom. Export is square.',
    aspect: 1,
    outputWidth: 512,
    outputHeight: 512,
    minWidth: 256,
    minHeight: 256,
    maxBytes: IMAGE_UPLOAD_MAX_BYTES,
    hint: `Square · ${512}×${512} recommended · Max ${IMAGE_UPLOAD_MAX_BYTES_LABEL}`,
    previewVariant: 'avatar',
  },
  'profile-banner': {
    id: 'profile-banner',
    label: 'Profile banner',
    cropTitle: 'Adjust banner',
    cropDescription: 'Drag to reposition and use the slider to zoom. Export uses a 3:1 wide frame.',
    ...BANNER_PRESET_BASE,
  },
  'circle-cover': {
    id: 'circle-cover',
    label: 'Circle cover',
    cropTitle: 'Adjust cover image',
    cropDescription: 'Drag to reposition and use the slider to zoom. Export uses a 3:1 wide frame.',
    ...BANNER_PRESET_BASE,
  },
  'circle-logo': {
    id: 'circle-logo',
    label: 'Circle logo',
    cropTitle: 'Adjust logo',
    cropDescription: 'Drag to reposition and use the slider to zoom. Export is square.',
    ...SQUARE_LOGO_PRESET_BASE,
  },
  'course-cover': {
    id: 'course-cover',
    label: 'Course cover',
    cropTitle: 'Adjust course cover',
    cropDescription: 'Drag to reposition and use the slider to zoom. Export uses a 3:1 wide frame.',
    ...BANNER_PRESET_BASE,
  },
};

export function getImageUploadPreset(id: ImageUploadPresetId): ImageUploadPreset {
  return IMAGE_UPLOAD_PRESETS[id];
}

export function isAllowedImageUploadMime(mime: string): boolean {
  return IMAGE_UPLOAD_ACCEPT.split(',').includes(mime.trim().toLowerCase());
}

export function validateImageUploadSize(
  byteLength: number,
  maxBytes: number = IMAGE_UPLOAD_MAX_BYTES,
): string | null {
  if (byteLength <= 0) return 'File is empty';
  if (byteLength > maxBytes) {
    return `Image must be ${IMAGE_UPLOAD_MAX_BYTES_LABEL} or smaller`;
  }
  return null;
}

export function validateImageUploadMime(mime: string): string | null {
  if (!mime) return 'Could not read file type';
  if (mime === 'image/gif') {
    return 'Animated GIF is not supported. Use JPEG, PNG, or WebP.';
  }
  if (!isAllowedImageUploadMime(mime)) {
    return `Use ${IMAGE_UPLOAD_ACCEPT_LABEL} only`;
  }
  return null;
}

export function validateImageUploadDimensions(
  width: number,
  height: number,
  preset: ImageUploadPreset,
): string | null {
  if (width < preset.minWidth || height < preset.minHeight) {
    return `Image is too small. Minimum ${preset.minWidth}×${preset.minHeight}px — choose a larger photo.`;
  }
  return null;
}

export function formatImageUploadBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
